import { useT } from "@transifex/react";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ToastMessage from "../../basic/toastMessage/ToastMessage";
import { useTestCreationContext } from "../../context/TestCreationContext";
import { ToastVariants } from "../../enums/toast";
import { useLogger } from "../../hooks/useLogger";
import useToast from "../../hooks/useToast";
import type { AssistantHandoffState } from "../../@types/anonymousFlow";
import { ANONYMOUS_FLOW_ANALYTICS_SOURCE } from "../../constants/AnonymousFlowConstants";
import type { SsoProvider } from "../../constants/SsoConstants";
import AccountCreatedPopup from "./AccountCreatedPopup";

const SSO_PROVIDER_NAMES: Record<SsoProvider, string> = { google: "Google", apple: "Apple", fobizz: "fobizz" };

type StoredHandoffState = AssistantHandoffState & { wasShown?: boolean };

/**
 * Feedback on /assistent after the anonymous flow: the account-created popup, a "test saved" toast once
 * AnonymousTestClaimer has linked a test, or a toast after a failed Google/Apple/fobizz sign-in.
 */
const AnonymousHandoffFeedback = () => {
    const t = useT();
    const history = useHistory();
    const location = useLocation<StoredHandoffState | undefined>();
    const { testContent, prepareFullTestDetails, resetAppStatus, isAnonymousTestClaimed } = useTestCreationContext();
    const { logEvent } = useLogger();
    const isSsoSignUpTracked = useRef(false);

    // Snapshot on mount.
    const [handoff] = useState<StoredHandoffState>(() => location.state ?? {});
    const isAccountCreated = !!handoff.anonymousAccountCreatedEmail || !!handoff.ssoAccountCreatedProvider;
    // Empty after a page load (the Google/Apple/fobizz round trip, or a reload); the popup then hides its PDF button.
    const [testDetails] = useState(() =>
        isAccountCreated && testContent.length > 0 ? prepareFullTestDetails(testContent) : undefined
    );
    const [isPopupOpen, setIsPopupOpen] = useState(isAccountCreated);

    const t_testSaved = t("Test saved", { _tags: "AssistantOverview, AccountCreatedPopup" });
    const t_ssoLoginFailed = t("Signing in with {provider} didn't work or was cancelled. Please try again.", {
        provider: handoff.ssoLoginFailedProvider ? SSO_PROVIDER_NAMES[handoff.ssoLoginFailedProvider] : "",
        _tags: "AssistantOverview, AccountCreatedPopup",
    });

    const testSavedToast = useToast();
    const ssoLoginFailedToast = useToast();

    useEffect(() => {
        // Only the popup's state stays in the history entry (until it's closed), so a reload (F5, or the one after a
        // cookie choice) brings back the popup but doesn't replay the toast or the event.
        if (handoff.wasShown) return;
        if (location.state) {
            history.replace({ ...location, state: isAccountCreated ? { ...handoff, wasShown: true } : undefined });
        }
        if (handoff.ssoLoginFailedProvider) {
            ssoLoginFailedToast.triggerToast();
        }
        // Sent here rather than from /auth: only after the login does the logger carry the teacher's role.
        // The ref keeps StrictMode's second effect run from sending it twice.
        if (handoff.ssoAccountCreatedProvider && !isSsoSignUpTracked.current) {
            isSsoSignUpTracked.current = true;
            logEvent("created account", {
                source: ANONYMOUS_FLOW_ANALYTICS_SOURCE,
                provider: handoff.ssoAccountCreatedProvider,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keyed on the claim rather than on router state, which wouldn't survive the full page load of an
    // external login. The registration popup says "test saved" itself, so it suppresses the toast.
    useEffect(() => {
        if (isAnonymousTestClaimed && !isAccountCreated) {
            testSavedToast.triggerToast();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnonymousTestClaimed]);

    const handlePopupClose = () => {
        setIsPopupOpen(false);
        history.replace({ ...location, state: undefined });
        resetAppStatus();
    };

    return (
        <>
            {isAccountCreated && (
                <AccountCreatedPopup
                    isOpen={isPopupOpen}
                    onClose={handlePopupClose}
                    email={handoff.anonymousAccountCreatedEmail}
                    testDetails={testDetails}
                />
            )}
            <ToastMessage
                text={t_testSaved}
                variant={ToastVariants.SUCCESS}
                isOpen={testSavedToast.isOpen}
                onOpenChange={testSavedToast.onOpenChange}
            />
            <ToastMessage
                text={t_ssoLoginFailed}
                variant={ToastVariants.ERROR}
                isOpen={ssoLoginFailedToast.isOpen}
                onOpenChange={ssoLoginFailedToast.onOpenChange}
            />
        </>
    );
};

export default AnonymousHandoffFeedback;
